// ============================================
// CERTIFICATES MANAGEMENT PAGE
// i18n: Faz 6D (v2.6)
// ============================================

import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  Plus,
  MoreVertical,
  Edit,
  Trash2,
  Award,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import type { CertificateType, Department } from '@crew-at-sea/shared-types';
import {
  PageHeader,
  DataTable,
  FilterBar,
  FilterSelect,
  StatusBadge,
  ConfirmDialog,
  CertificateTypeModal,
} from '@/components';
import { adminApi } from '@/services/api';
import { cn } from '@/utils';
import { useTranslation } from '@crew-at-sea/i18n';

export const Route = createFileRoute('/_authenticated/certificates/')({
  component: CertificatesPage,
});

function CertificatesPage() {
  const { t } = useTranslation(['panel', 'common', 'enums']);
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('');
  const [modalState, setModalState] = useState<{
    open: boolean;
    editData: CertificateType | null;
  }>({ open: false, editData: null });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    certificate: CertificateType | null;
  }>({ open: false, certificate: null });

  // Sertifika tiplerini getir
  const { data, isLoading } = useQuery<CertificateType[]>({
    queryKey: ['admin', 'certificate-types'],
    queryFn: () => adminApi.getCertificateTypes(),
    placeholderData: [
      {
        id: '1',
        code: 'COC',
        nameEn: 'Certificate of Competency',
        nameTr: 'Yeterlilik Belgesi',
        category: 'STCW',
        description: 'Main officer certificate',
        issuingAuthority: 'Turkish Maritime Administration',
        validityPeriod: 60,
        isRequired: true,
        department: 'DECK' as Department,
        sortOrder: 1,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: '2',
        code: 'GMDSS',
        nameEn: 'GMDSS General Operator Certificate',
        nameTr: 'GMDSS Genel Operatör Belgesi',
        category: 'STCW',
        description: 'Global Maritime Distress and Safety System',
        issuingAuthority: 'Turkish Maritime Administration',
        validityPeriod: 60,
        isRequired: true,
        department: 'DECK' as Department,
        sortOrder: 2,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: '3',
        code: 'BST',
        nameEn: 'Basic Safety Training',
        nameTr: 'Temel Güvenlik Eğitimi',
        category: 'STCW',
        description: 'Basic safety training certificate',
        issuingAuthority: 'Approved Training Center',
        validityPeriod: 60,
        isRequired: true,
        department: null,
        sortOrder: 3,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
      {
        id: '4',
        code: 'ECDIS',
        nameEn: 'ECDIS Certificate',
        nameTr: 'ECDIS Belgesi',
        category: 'Navigation',
        description: 'Electronic Chart Display and Information System',
        issuingAuthority: 'Approved Training Center',
        validityPeriod: null,
        isRequired: false,
        department: 'DECK' as Department,
        sortOrder: 4,
        isActive: true,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ] as CertificateType[],
  });

  const columns = [
    {
      key: 'certificate',
      header: t('panel:certificates.certificate'),
      render: (cert: CertificateType) => (
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-maritime bg-amber-100 text-amber-700">
            <Award className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{cert.nameTr}</p>
            <p className="text-xs text-gray-500">{cert.code}</p>
          </div>
        </div>
      ),
    },
    {
      key: 'category',
      header: t('panel:certificates.category'),
      render: (cert: CertificateType) => (
        <span className="px-2.5 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-800">
          {cert.category}
        </span>
      ),
    },
    {
      key: 'department',
      header: t('panel:certificates.department'),
      render: (cert: CertificateType) => (
        <span
          className={cn(
            'px-2.5 py-0.5 text-xs font-medium rounded-full',
            cert.department === 'DECK' && 'bg-blue-100 text-blue-800',
            cert.department === 'ENGINE' && 'bg-green-100 text-green-800',
            cert.department === 'ELECTRICAL' && 'bg-amber-100 text-amber-800',
            cert.department === 'CATERING' && 'bg-purple-100 text-purple-800',
            !cert.department && 'bg-gray-100 text-gray-600'
          )}
        >
          {cert.department ? t(`enums:department.${cert.department}`, { defaultValue: cert.department }) : t('panel:certificates.general')}
        </span>
      ),
    },
    {
      key: 'validity',
      header: t('panel:certificates.validity'),
      render: (cert: CertificateType) => (
        <span className="text-gray-600">
          {cert.validityPeriod ? t('panel:certificates.validityMonths', { count: cert.validityPeriod }) : t('panel:certificates.unlimited')}
        </span>
      ),
    },
    {
      key: 'required',
      header: t('panel:certificates.required'),
      render: (cert: CertificateType) => (
        <span
          className={cn(
            'inline-flex items-center gap-1 text-sm',
            cert.isRequired ? 'text-green-600' : 'text-gray-400'
          )}
        >
          {cert.isRequired ? (
            <>
              <CheckCircle className="h-4 w-4" />
              {t('common:yes')}
            </>
          ) : (
            <>
              <XCircle className="h-4 w-4" />
              {t('common:no')}
            </>
          )}
        </span>
      ),
    },
    {
      key: 'status',
      header: t('common:status'),
      render: (cert: CertificateType) => (
        <StatusBadge
          status={cert.isActive ? 'ACTIVE' : 'INACTIVE'}
          dot
        />
      ),
    },
    {
      key: 'actions',
      header: '',
      width: '80px',
      render: (cert: CertificateType) => (
        <div className="relative group">
          <button className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-md">
            <MoreVertical className="h-4 w-4" />
          </button>
          <div className="absolute right-0 top-full mt-1 w-48 bg-white border border-gray-200 rounded-maritime shadow-maritime-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-10">
            <div className="py-1">
              <button 
                onClick={() => setModalState({ open: true, editData: cert })}
                className="flex items-center gap-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full"
              >
                <Edit className="h-4 w-4" />
                {t('common:edit')}
              </button>
              <hr className="my-1" />
              <button
                onClick={() => setDeleteDialog({ open: true, certificate: cert })}
                className="flex items-center gap-2 px-4 py-2 text-sm text-red-600 hover:bg-red-50 w-full"
              >
                <Trash2 className="h-4 w-4" />
                {t('common:delete')}
              </button>
            </div>
          </div>
        </div>
      ),
    },
  ];

  const categoryOptions = [
    { value: 'STCW', label: 'STCW' },
    { value: 'Navigation', label: 'Navigation' },
    { value: 'Safety', label: 'Safety' },
    { value: 'Medical', label: 'Medical' },
    { value: 'Other', label: t('enums:department.OTHER') },
  ];

  const departmentOptions = [
    { value: 'DECK', label: t('enums:department.DECK') },
    { value: 'ENGINE', label: t('enums:department.ENGINE') },
    { value: 'ELECTRICAL', label: t('enums:department.ELECTRICAL') },
    { value: 'CATERING', label: t('enums:department.CATERING') },
  ];

  // Veri filtreleme
  const certificatesArray = Array.isArray(data) ? data : ((data as any)?.data || []);
  const filteredData = certificatesArray.filter((cert: any) => {
    const matchesSearch =
      !search ||
      cert.nameTr?.toLowerCase().includes(search.toLowerCase()) ||
      cert.nameEn?.toLowerCase().includes(search.toLowerCase()) ||
      cert.code?.toLowerCase().includes(search.toLowerCase());
    const matchesCategory = !categoryFilter || cert.category === categoryFilter;
    const matchesDepartment = !departmentFilter || cert.department === departmentFilter;
    return matchesSearch && matchesCategory && matchesDepartment;
  });

  const activeFiltersCount =
    (categoryFilter ? 1 : 0) + (departmentFilter ? 1 : 0) + (search ? 1 : 0);

  return (
    <div className="space-y-6">
      <PageHeader
        title={t('panel:certificates.title')}
        description={t('panel:certificates.description')}
        actions={
          <button 
            onClick={() => setModalState({ open: true, editData: null })}
            className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700 transition-colors"
          >
            <Plus className="h-4 w-4" />
            {t('panel:certificates.createCertType')}
          </button>
        }
      />

      <FilterBar
        searchValue={search}
        onSearchChange={setSearch}
        searchPlaceholder={t('panel:certificates.searchPlaceholder')}
        filters={
          <>
            <FilterSelect
              value={categoryFilter}
              onChange={setCategoryFilter}
              options={categoryOptions}
              placeholder={t('panel:certificates.allCategories')}
            />
            <FilterSelect
              value={departmentFilter}
              onChange={setDepartmentFilter}
              options={departmentOptions}
              placeholder={t('panel:certificates.allDepartments')}
            />
          </>
        }
        activeFiltersCount={activeFiltersCount}
        onClearFilters={() => {
          setSearch('');
          setCategoryFilter('');
          setDepartmentFilter('');
        }}
      />

      <DataTable
        columns={columns}
        data={filteredData}
        keyExtractor={(cert) => cert.id}
        loading={isLoading}
        emptyMessage={t('panel:certificates.noCertTypes')}
      />

      <ConfirmDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}
        title={t('panel:certificates.deleteTitle')}
        description={t('panel:certificates.deleteDescription', { name: deleteDialog.certificate?.nameTr })}
        variant="danger"
        confirmText={t('common:delete')}
        onConfirm={() => {
          if (deleteDialog.certificate) {
            adminApi.deleteCertificateType(deleteDialog.certificate.id)
              .then(() => {
                queryClient.invalidateQueries({ queryKey: ['admin', 'certificate-types'] });
                setDeleteDialog({ open: false, certificate: null });
              })
              .catch((error: any) => {
                const message = error?.response?.data?.message || 'Silme işlemi başarısız';
                alert(message);
                setDeleteDialog({ open: false, certificate: null });
              });
          }
        }}
      />

      <CertificateTypeModal
        open={modalState.open}
        onClose={() => setModalState({ open: false, editData: null })}
        editData={modalState.editData}
      />
    </div>
  );
}
